import { memo, useState } from 'react';
import {
  Box, Button, TextField, Typography, Switch, IconButton, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Select, MenuItem, FormControl, InputLabel, Collapse, Tabs, Tab,
} from '@mui/material';
import SettingsIcon      from '@mui/icons-material/Settings';
import CloseIcon         from '@mui/icons-material/Close';
import CommentIcon       from '@mui/icons-material/Comment';
import ImageIcon         from '@mui/icons-material/Image';
import PollIcon          from '@mui/icons-material/Poll';
import AutoAwesomeIcon   from '@mui/icons-material/AutoAwesome';
import AddIcon           from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';

import SelectBackground from './SelectBackground';

// Operadores soportados para las reglas de interpretación del resultado.
// 'between' = rango inclusivo entre value ("Desde") y valueTo ("Hasta").
const RESULT_OPERATORS = ['=', '<', '>', 'between'];

// ── Toggle compacto (showLogo / showComment) para la fila de 2 columnas ──
const CompactToggle = memo(({ icon, checked, label, statusText, statusColor, name, onChange, color, disabled }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    py: 1.25, px: 1.25, borderRadius: 2,
    bgcolor: checked ? `rgba(0,0,0,0.03)` : 'grey.50',
    border: '1px solid',
    borderColor: checked ? `${color}.200` : 'grey.200',
    opacity: disabled ? 0.65 : 1,
    minWidth: 0,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{label}</Typography>
        <Typography variant="caption" color={statusColor} noWrap sx={{ display: 'block' }}>
          {statusText}
        </Typography>
      </Box>
    </Box>
    <Switch
      checked={checked}
      onChange={onChange}
      name={name}
      color={color}
      size="medium"
      disabled={disabled}
    />
  </Box>
));
CompactToggle.displayName = 'CompactToggle';

// ── Panel de pestaña genérico (evita renderizar contenido oculto innecesariamente) ──
function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box role="tabpanel">{children}</Box>;
}

{/*Modal para seleccionar opciones especiales, organizado en 2 pestañas:
  - "Diseño" (t.settingsTabDesign): logo, comentarios, estilo/color de fondo.
  - "Evaluación" (t.settingsTabEvaluate): resultado final + reglas de
    interpretación editables (operador —incluyendo rango "entre"—, valor(es),
    interpretación y detalles), dependiendo de la suscripción.*/}
const SurveySettingsModal = memo(({
  open, onClose, survey, handleChange, handleStyleChange, subscriptionActive,
  onResultRuleChange, onAddResultRule, onRemoveResultRule, t,
}) => {
  const resultRules = survey.resultRules || [];
  const [rulesExpanded, setRulesExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: { xs: '80vh', sm: 560 },
          maxHeight: { xs: '90vh', sm: 640 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
        <SettingsIcon fontSize="small" />
        <Typography component="span" variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
          {t.settingsModalTitle}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }} aria-label={t.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<AutoAwesomeIcon fontSize="small" />}
          iconPosition="start"
          label={t.settingsTabDesign}
        />
        <Tab
          icon={<PollIcon fontSize="small" />}
          iconPosition="start"
          label={t.settingsTabEvaluate}
        />
      </Tabs>

      <DialogContent
        sx={{
          pt: 2.5,
          pb: 1,
          flex: 1,
          minHeight: 0, // permite que overflowY funcione dentro de un flex child
          overflowY: 'auto',
        }}
      >

        {/* ═══════════════ TAB 0: DISEÑO ═══════════════ */}
        <TabPanel value={activeTab} index={0}>
          {/* — showLogo + showComment en una sola fila — */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
            mb: 2,
          }}>
            <CompactToggle
              icon={<ImageIcon color={survey.showLogo ? 'primary' : 'disabled'} />}
              checked={survey.showLogo}
              name="showLogo"
              color="primary"
              onChange={handleChange}
              disabled={!subscriptionActive}
              label={t.showLogoLabel}
              statusColor={subscriptionActive ? 'text.secondary' : 'error'}
              statusText={
                !subscriptionActive
                  ? t.requiresSubscription
                  : survey.showLogo ? t.logoVisible : t.logoHidden
              }
            />
            {/* ← FIX: showComment ya NO requiere suscripción. Se quitó
                disabled={!subscriptionActive} y el statusText/statusColor
                que mostraban el aviso "requiere suscripción". El logo
                (arriba) se mantiene restringido a plan de pago. */}
            <CompactToggle
              icon={<CommentIcon color={survey.showComment ? 'secondary' : 'disabled'} />}
              checked={survey.showComment}
              name="showComment"
              color="secondary"
              onChange={handleChange}
              label={t.enableCommentsLabel}
              statusColor="text.secondary"
              statusText={survey.showComment ? t.commentsVisible : t.commentsDisabled}
            />
          </Box>

          {/* — Estilo de fondo — */}
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AutoAwesomeIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={600} color="primary.main">
              {t.styleLabel}
            </Typography>
          </Box>
          <SelectBackground value={survey.style || 'bg-white'} onChange={handleStyleChange} />
        </TabPanel>

        {/* ═══════════════ TAB 1: EVALUACIÓN ═══════════════ */}
        <TabPanel value={activeTab} index={1}>
          {/* — showResult — */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            py: 1.5, px: 1.5, borderRadius: 2, mb: survey.showResult && subscriptionActive ? 1 : 2,
            bgcolor: survey.showResult ? 'rgba(2,136,209,0.06)' : 'grey.50',
            border: '1px solid',
            borderColor: survey.showResult ? 'info.200' : 'grey.200',
            opacity: subscriptionActive ? 1 : 0.65,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PollIcon color={survey.showResult ? 'info' : 'disabled'} />
              <Box>
                <Typography variant="body2" fontWeight={600}>{t.showResultLabel}</Typography>
                <Typography variant="caption" color={subscriptionActive ? 'text.secondary' : 'error'}>
                  {!subscriptionActive
                    ? t.requiresSubscription
                    : survey.showResult ? t.resultVisible : t.resultHidden}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={survey.showResult}
              onChange={handleChange}
              name="showResult"
              color="info"
              size="medium"
              disabled={!subscriptionActive}
            />
          </Box>

          {/* — Reglas de interpretación del resultado (colapsable) — */}
          {/* Solo visible si showResult está activo y la cuenta es de paga.  */}
          {/* Soporta igualdad, menor/mayor que, y rango ("Entre" Desde-Hasta). */}
          {/* Ejemplo: Resultado 1: Entre 1 y 2, interpretación:"Muy mal" */}
          {survey.showResult && subscriptionActive && (
            <Box sx={{
              mb: 2, mt: 0.5, borderRadius: 2,
              bgcolor: 'rgba(2,136,209,0.03)', border: '1px dashed', borderColor: 'info.200',
              overflow: 'hidden',
            }}>
              <Box
                onClick={() => setRulesExpanded((v) => !v)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 1.25, cursor: 'pointer', userSelect: 'none',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} color="info.main">
                    {t.resultCustomizeTitle}
                  </Typography>
                  {!rulesExpanded && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {resultRules.length} {t.resultRulesCountSuffix}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label={rulesExpanded ? t.collapseAriaLabel : t.expandAriaLabel}
                  sx={{
                    transform: rulesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    flexShrink: 0,
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Box>

              <Collapse in={rulesExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                    {t.resultLabelsHelper}
                  </Typography>

                  {resultRules.map((rule, idx) => {
                    const isBetween = rule.operator === 'between';
                    return (
                      <Box
                        key={rule.id}
                        sx={{
                          mb: 1.5, pb: 1.5,
                          borderBottom: idx < resultRules.length - 1 ? '1px dashed' : 'none',
                          borderColor: 'info.100',
                        }}
                      >
                        {/* Fila 1: nivel, operador, valor(es), interpretación, eliminar */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1, flexWrap: 'wrap' }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 66, pt: 1.25, flexShrink: 0 }}
                          >
                            {t.resultLevelLabel} {idx + 1}
                          </Typography>

                          <FormControl size="small" sx={{ width: 100, flexShrink: 0 }}>
                            <InputLabel id={`op-label-${rule.id}`}>{t.resultOperatorLabel}</InputLabel>
                            <Select
                              labelId={`op-label-${rule.id}`}
                              label={t.resultOperatorLabel}
                              value={rule.operator}
                              onChange={(e) => onResultRuleChange(rule.id, 'operator', e.target.value)}
                              sx={{ borderRadius: 1.5 }}
                            >
                              {RESULT_OPERATORS.map((op) => (
                                <MenuItem key={op} value={op}>
                                  {op === 'between' ? t.resultOperatorBetween : op}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <TextField
                            size="small"
                            type="number"
                            label={isBetween ? t.resultValueFromLabel : t.resultValueLabel}
                            value={rule.value}
                            onChange={(e) => onResultRuleChange(rule.id, 'value', e.target.value)}
                            sx={{ width: 90, flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />

                          {isBetween && (
                            <TextField
                              size="small"
                              type="number"
                              label={t.resultValueToLabel}
                              value={rule.valueTo}
                              onChange={(e) => onResultRuleChange(rule.id, 'valueTo', e.target.value)}
                              sx={{ width: 90, flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                            />
                          )}

                          <TextField
                            size="small"
                            fullWidth
                            label={t.resultInterpretationLabel}
                            placeholder={t.defaultResultLabels[idx] ?? ''}
                            value={rule.label}
                            onChange={(e) => onResultRuleChange(rule.id, 'label', e.target.value)}
                            sx={{ minWidth: 160, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />

                          <IconButton
                            size="small"
                            onClick={() => onRemoveResultRule(rule.id)}
                            disabled={resultRules.length <= 1}
                            aria-label={t.removeRuleAriaLabel}
                            sx={{ mt: 0.5, flexShrink: 0 }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Fila 2: detalles (texto largo opcional) */}
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          label={t.resultDetailsLabel}
                          placeholder={t.resultDetailsPlaceholder}
                          value={rule.detalles || ''}
                          onChange={(e) => onResultRuleChange(rule.id, 'detalles', e.target.value)}
                          sx={{ pl: { sm: '74px' }, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Box>
                    );
                  })}

                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAddResultRule}
                    sx={{ mt: 0.5 }}
                  >
                    {t.addRuleButton}
                  </Button>
                </Box>
              </Collapse>
            </Box>
          )}
        </TabPanel>

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="contained" color="primary" fullWidth>
          {t.acceptButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

SurveySettingsModal.displayName = 'SurveySettingsModal';

export default SurveySettingsModal;